/* =====================================================================
 * Rebo Mode Store — 에이전트 응답 모드 전역 상태
 *
 * auto  : 에이전트가 판단하여 해결 (기본값)
 * fast  : 빠르게 답변
 * plan  : 복잡한 문제 해결
 * ===================================================================== */

import { create } from 'zustand';
import i18n from '@auw/i18n';

export type McpApprovalMode = 'ask_for_approval' | 'full_access';

export type ReboMode = 'auto' | 'fast' | 'plan' | 'skill';

export interface ReboModeOption {
  id: ReboMode;
  label: string;
  description: string;
}

export function getReboModeOptions(): ReboModeOption[] {
  return [
    { id: 'auto', label: i18n.t('common:reboMode.autoLabel'), description: i18n.t('common:reboMode.autoDesc') },
    { id: 'fast', label: i18n.t('common:reboMode.fastLabel'), description: i18n.t('common:reboMode.fastDesc') },
    { id: 'plan', label: i18n.t('common:reboMode.planLabel'), description: i18n.t('common:reboMode.deepDesc') },
    { id: 'skill', label: i18n.t('common:reboMode.skillLabel'), description: i18n.t('common:reboMode.skillDesc') },
  ];
}

/* ---------------------------------------------------------------------
 * Interactive (Human-in-the-loop) 토글
 *
 * on 일 때만 에이전트가 작업을 멈추고 사용자에게 선택지를 되묻는다.
 * 모드(auto/fast/plan/skill)와 직교하는 축이므로 별도 플래그로 둔다.
 * ------------------------------------------------------------------- */

const INTERACTIVE_MODE_STORAGE_KEY = 'auw.chat.interactiveMode';
const REASONING_MODE_STORAGE_KEY = 'auw.chat.reasoningEnabled';

function readStoredInteractive(): boolean {
  try {
    return localStorage.getItem(INTERACTIVE_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistInteractive(next: boolean): void {
  try {
    localStorage.setItem(INTERACTIVE_MODE_STORAGE_KEY, String(next));
  } catch {
    /* storage 접근 실패는 무시 — 토글은 세션 내에서 계속 동작한다 */
  }
}

function readStoredReasoning(): boolean {
  try {
    const stored = localStorage.getItem(REASONING_MODE_STORAGE_KEY);
    return stored === null ? true : stored !== 'false';
  } catch {
    return true;
  }
}

function persistReasoning(next: boolean): void {
  try {
    localStorage.setItem(REASONING_MODE_STORAGE_KEY, String(next));
  } catch {
    /* storage 접근 실패는 무시 — 토글은 세션 내에서 계속 동작한다 */
  }
}

interface ReboModeState {
  mcpApprovalMode: McpApprovalMode;
  setMcpApprovalMode: (mode: McpApprovalMode) => void;
  mode: ReboMode;
  setMode: (mode: ReboMode) => void;
  /** Interactive(HITL) 모드 on/off. 기본 off. */
  interactive: boolean;
  setInteractive: (interactive: boolean) => void;
  toggleInteractive: () => void;
  /** 요청 단위 reasoning on/off. 기본 on. */
  reasoningEnabled: boolean;
  setReasoningEnabled: (reasoningEnabled: boolean) => void;
  toggleReasoning: () => void;
}

export const useReboModeStore = create<ReboModeState>((set, get) => ({
  mcpApprovalMode: 'ask_for_approval',
  setMcpApprovalMode: (mcpApprovalMode) => set({ mcpApprovalMode }),
  mode: 'auto',
  setMode: (mode) => set({ mode }),
  interactive: readStoredInteractive(),
  setInteractive: (interactive) => {
    persistInteractive(interactive);
    set({ interactive });
  },
  toggleInteractive: () => {
    const next = !get().interactive;
    persistInteractive(next);
    set({ interactive: next });
  },
  reasoningEnabled: readStoredReasoning(),
  setReasoningEnabled: (reasoningEnabled) => {
    persistReasoning(reasoningEnabled);
    set({ reasoningEnabled });
  },
  toggleReasoning: () => {
    const next = !get().reasoningEnabled;
    persistReasoning(next);
    set({ reasoningEnabled: next });
  },
}));
